import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { useQuote } from "@/context/QuoteContext";
import { SERVICES } from "@/data/site";
import client, { formatApiError } from "@/lib/api";

const SECTORS = ["Automotriz", "Industrial", "Naval", "Suministros"];
const EMPTY = { name: "", email: "", phone: "", company: "", sector: "", service: "", message: "" };

const inputCls =
  "bg-gema-surface border-white/15 text-white placeholder:text-white/30 rounded-none focus-visible:ring-gema-red";

export default function QuoteDialog() {
  const { open, setOpen, prefill } = useQuote();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY, service: prefill.service || "", sector: prefill.sector || "" });
      setErrors({});
    }
  }, [open, prefill]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Ingresa tu nombre";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Correo inválido";
    if (!form.phone.trim()) e.phone = "Ingresa tu teléfono";
    if (!form.sector) e.sector = "Selecciona un sector";
    if (!form.message.trim()) e.message = "Describe tu necesidad";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await client.post("/quotes", form);
      toast.success("Solicitud enviada", { description: "Nuestro equipo te contactará pronto." });
      setOpen(false);
    } catch (err) {
      toast.error("No se pudo enviar", { description: formatApiError(err.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="quote-dialog"
        className="bg-gema-ink border border-white/10 text-white rounded-none max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-black uppercase">Solicitar Cotización</DialogTitle>
          <DialogDescription className="text-white/50">
            Cuéntanos qué necesitas y te enviaremos una propuesta a la medida.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="quote-form" noValidate>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-white/70 text-xs uppercase tracking-wider">Nombre *</Label>
              <Input data-testid="quote-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} />
              {errors.name && <p className="text-gema-red text-xs mt-1">{errors.name}</p>}
            </div>
            <div>
              <Label className="text-white/70 text-xs uppercase tracking-wider">Teléfono *</Label>
              <Input data-testid="quote-phone" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
              {errors.phone && <p className="text-gema-red text-xs mt-1">{errors.phone}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-white/70 text-xs uppercase tracking-wider">Correo *</Label>
              <Input data-testid="quote-email" type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} />
              {errors.email && <p className="text-gema-red text-xs mt-1">{errors.email}</p>}
            </div>
            <div>
              <Label className="text-white/70 text-xs uppercase tracking-wider">Empresa</Label>
              <Input data-testid="quote-company" className={inputCls} value={form.company} onChange={(e) => set("company", e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-white/70 text-xs uppercase tracking-wider">Sector *</Label>
              <Select value={form.sector} onValueChange={(v) => set("sector", v)}>
                <SelectTrigger data-testid="quote-sector" className={inputCls}>
                  <SelectValue placeholder="Selecciona" />
                </SelectTrigger>
                <SelectContent className="bg-gema-ink border-white/15 text-white">
                  {SECTORS.map((s) => (
                    <SelectItem key={s} value={s} className="focus:bg-gema-red focus:text-white">{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.sector && <p className="text-gema-red text-xs mt-1">{errors.sector}</p>}
            </div>
            <div>
              <Label className="text-white/70 text-xs uppercase tracking-wider">Servicio</Label>
              <Select value={form.service} onValueChange={(v) => set("service", v)}>
                <SelectTrigger data-testid="quote-service" className={inputCls}>
                  <SelectValue placeholder="Opcional" />
                </SelectTrigger>
                <SelectContent className="bg-gema-ink border-white/15 text-white">
                  {SERVICES.map((s) => (
                    <SelectItem key={s.slug} value={s.name} className="focus:bg-gema-red focus:text-white">{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-white/70 text-xs uppercase tracking-wider">Detalle *</Label>
            <Textarea data-testid="quote-message" rows={4} className={inputCls} value={form.message} onChange={(e) => set("message", e.target.value)} />
            {errors.message && <p className="text-gema-red text-xs mt-1">{errors.message}</p>}
          </div>

          <button type="submit" disabled={loading} data-testid="quote-submit" className="btn-primary w-full disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enviar Solicitud"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
